import { getYouTubeEmbedId, isRumbleEmbedSrc } from './embeds';
import { resolveMediaUrl } from './media';
import { getYouTubeThumbnailUrl } from './video';

export const MAX_THUMBNAIL_SUGGESTIONS = 8;

// http(s), protocol-relative, or server-relative paths only — never data:,
// blob: or other schemes.
const isUsableImageUrl = (value) => (
    typeof value === 'string'
    && (/^https?:\/\//i.test(value.trim()) || /^\/(?:[^/]|\/[^/])/.test(value.trim()))
);

const youTubeStill = (src) => {
    const id = getYouTubeEmbedId(src);
    return id ? getYouTubeThumbnailUrl(id) : null;
};

const shortLabel = (text, max = 40) => {
    const clean = String(text || '').replace(/\s+/g, ' ').trim();
    return clean.length > max ? `${clean.slice(0, max - 1)}…` : clean;
};

const collectSuggestions = (html) => {
    if (typeof html !== 'string' || !/<(?:img|video|iframe)\b/i.test(html)
        || typeof DOMParser === 'undefined') {
        return [];
    }

    // An inert document: nothing in it loads or runs.
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const videoStills = [];
    const photos = [];

    doc.querySelectorAll('iframe, video, img').forEach((element) => {
        const tag = element.tagName.toLowerCase();
        if (tag === 'iframe') {
            const src = element.getAttribute('src') || '';
            const provider = isRumbleEmbedSrc(src) ? 'Rumble' : 'YouTube';
            videoStills.push({ value: element.getAttribute('data-poster'), label: `${provider} thumbnail` });
            videoStills.push({ value: youTubeStill(src), label: 'YouTube thumbnail' });
        } else if (tag === 'video') {
            videoStills.push({ value: element.getAttribute('poster'), label: 'Video poster' });
        } else {
            const alt = shortLabel(element.getAttribute('alt'));
            photos.push({ value: element.getAttribute('src'), label: alt ? `Photo — ${alt}` : 'Photo' });
        }
    });

    // Video stills first — a post's video is the usual card image — then
    // photos, each in document order.
    const seen = new Set();
    const suggestions = [];
    [...videoStills, ...photos].forEach(({ value, label }) => {
        if (suggestions.length >= MAX_THUMBNAIL_SUGGESTIONS || !isUsableImageUrl(value)) {
            return;
        }
        const stored = value.trim();
        const src = resolveMediaUrl(stored);
        if (!src || seen.has(src)) {
            return;
        }
        seen.add(src);
        suggestions.push({ src, value: stored, label });
    });
    return suggestions;
};

// The portal re-renders on every keystroke; parse each distinct body once
// (and keep the array identity stable while the body is unchanged).
let lastHtml = null;
let lastSuggestions = [];

/**
 * Thumbnail candidates found in an article body, for ThumbnailPicker's
 * "Use an image from this post" tiles: YouTube embeds (the platform's
 * hqdefault still), embed posters (iframe data-poster), uploaded-video
 * posters and inline photos — deduped, at most 8. Each entry is
 * { src, value, label }: `src` resolved for display (resolveMediaUrl),
 * `value` the portable form as stored in the body, which becomes the
 * thumbnail when picked.
 */
export const extractThumbnailSuggestions = (html) => {
    if (html === lastHtml) {
        return lastSuggestions;
    }
    lastHtml = html;
    lastSuggestions = collectSuggestions(html);
    return lastSuggestions;
};
